export interface PropertyConfig {
  description: string;
  operators: string[];
  examples: string[];
  related: string[];
  regex?: string;
}

export const propertyConfig: Record<string, PropertyConfig> = {
  "AD site": {
    description:
      "Matches the name of a subnet the endpoint is connected to, as defined by Active Directory Sites and Services. Only Active Directory domain members can match an AD site. Regular expressions are allowed and must match the full site name. This condition supports writing the value alone.",
    operators: ["is", "is not", "=", "!"],
    examples: ["AD site = Azure.*", "Azure.*"],
    related: ["site"],
    regex: "^(?:[A-Z][A-Z0-9-]{0,61}[A-Z0-9])$",
  },
  "AD-joined": {
    description: "Any device which is a Microsoft Active Directory domain member.",
    operators: [],
    examples: [],
    related: ["Entra-joined"],
  },
  "Entra-joined": {
    description:
      "Any device which has established a trust relationship and is managed centrally with Microsoft Entra ID, as opposed to an Entra-registered device which is associated with Entra ID but remains primarily managed by a personal account.",
    operators: [],
    examples: [],
    related: ["AD-joined"],
  },
  "BitLocker OS": {
    description: "Any device running a version of Windows which supports BitLocker drive encryption.",
    operators: [],
    examples: [],
    related: [],
  },
  "blade chassis": {
    description: "Any device with a blade chassis system enclosure (chassis type 2)",
    operators: [],
    examples: [],
    related: ["chassis type", "desktop", "laptop"],
  },
  "chassis type": {
    description:
      "Matches the primary chassis type number provided by the device firmware, ignoring additionally connected chassis types such as a docking station.",
    operators: ["is", "is not", "=", "!"],
    examples: ["chassis type = 23"],
    related: ["blade chassis", "desktop", "laptop"],
    regex: "^[0-9]{1,2}$",
  },
  "device name" : {
    description: "Matches the hostname or computer name of the endpoint, not including the DNS suffix. Regular expressions are allowed and must match the full device name.",
    operators: ["is", "is not", "=", "!"],
    examples: ["Natalie-PC", "NA"],
    related: [],
  },
  desktop: {
    description: "Any device with a desktop-class system enclosure (chassis type 3, 4, 5, 6, 7, 15, 16).",
    operators: [],
    examples: [],
    related: ["blade chassis", "chassis type", "laptop"],
  },
  "during an OS deployment": {
    description: "Any time during an Aiden-managed OS deployment, including Aiden’s OS deployment auto-logon phase and any Autopilot phase",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during Autopilot, during ESP Account Setup, during ESP Device Setup, during the enrollment status page, during Windows Setup"],
  },
  "during Autopilot": {
    description: "Any time during Windows Autopilot, including task sequences which run during a reboot between Autopilot phases.",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during an OS deployment, during ESP Account Setup, during ESP Device Setup, during the enrollment status page, during Windows Setup"],
  },
  "during ESP Account Setup": {
    description: "Any time during the EspAccountSetup phase of the Autopilot Enrollment Status Page (ESP), except when AidenBot uses the Windows Task Scheduler to run another copy of itself independently from Autopilot during the EspAccountSetup phase.",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during an OS deployment, during Autopilot, during ESP Device Setup, during the enrollment status page, during Windows Setup"],
  },
  "during ESP Device Setup": {
    description: "Any time during the EspDeviceSetup phase of the Autopilot Enrollment Status Page (ESP), except when AidenBot uses the /start-before-logon feature to run another copy of itself independently from Autopilot during the SystemSetupDuringOsd phase.",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during an OS deployment, during Autopilot, during ESP Account Setup, during the enrollment status page, during Windows Setup"],
  },
  "during the enrollment status page": {
    description:
      "Any time during the EspDeviceSetup or EspAccountSetup phases of the Autopilot Enrollment Status Page (ESP), when Microsoft Intune may run a package – If this phrase is applied to any package applicability rule, only packages which include this phrase will be considered for running during the ESP. This phrase will not limit packages when the /start-before-logon option is enabled for Autopilot, which enables full-provisioning mode outside of the ESP.",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during an OS deployment, during Autopilot, during ESP Account Setup, during ESP Device Setup, during Windows Setup"],
  },
  "during Windows Setup": {
    description:
      "Any time during the Windows startup and installation sequences, including the SetupCompleteOsdScript, SystemSetupDuringOsd, and Autopilot EspDeviceSetup phases, but not including phases afterwards such as the Autopilot EspAccountSetup or auto-logon phases",
    operators: [],
    examples: [],
    related: ["allowed during the enrollment status page, during an OS deployment, during Autopilot, during ESP Account Setup, during ESP Device Setup, during the enrollment status page"],
  },
  endpoint: {
    description: "Any device capable of running AidenBot, including desktops, laptops, servers, VMs, VDI, etc.",
    operators: [],
    examples: [],
    related: ["server", "workstation", "VDI", "VM"],
  },
  installed: {
    description:
      "Matches installed products, Windows features, Store apps, and system-wide fonts. Allows Transposed Property=Value Pairs, e.g. 'Microsoft Office is installed' instead of 'installed = Microsoft Office'. Accepts version comparison operators: <=, <, =, >, >=, ! Versions may be specified by including a colon after the product name, followed by the version.",
    operators: ["is", "is not", "<=", "<", "=", ">", ">=", "!"],
    examples: [
      "Oracle Java 8.0.1690.0 is for any workstation when installed <= My Old App:5.2",
      "Oracle Java 8.0.1690.0 is for any workstation when My Old App is installed",
    ],
    related: ["visibly installed"],
  },
  interactive: {
    description:
      "Runs a package visible to the end user. May pause if an error occurs or to prompt for input. AidenBot must be running in interactive mode within an interactive user environment. Packages run in silent mode by default.",
    operators: [],
    examples: [],
    related: ["unattended"],
  },
  "it will break auto-logon": {
    description:
      "Causes AidenBot to postpone running the applicable package until auto-logon is no longer enabled or no longer needed to complete an OS deployment.",
    operators: [],
    examples: [],
    related: [],
  },
  laptop: {
    description: "Any device with a mobile or laptop-class system enclosure (chassis type 8, 9, 10, 14, 31, 32).",
    operators: [],
    examples: [],
    related: ["blade chassis", "chassis type", "desktop"],
  },
  manufacturer: {
    description:
      "Matches the name of the manufacturer value provided by the device firmware. This condition supports writing the value alone. See the examples.",
    operators: ["is", "is not", "=", "!"],
    examples: ["manufacturer = Microsoft", "Microsoft"],
    related: [],
  },
  marketing: {
    description: "",
    operators: [],
    examples: [],
    related: [],
  },
  model: {
    description:
      "Matches the name of the system model value provided by the device firmware. This condition supports writing the value alone.",
    operators: ["is", "is not", "=", "!"],
    examples: ["model = Surface Pro 5", "Surface Pro 5"],
    related: [],
  },
  "NetBIOS-enabled network interface": {
    description: "Any network adapter with the NetBIOS feature enabled.",
    operators: [],
    examples: [],
    related: [],
  },
  "Office architecture": {
    description: "Matches the architecture of the installed version of Microsoft 365 Apps, a.k.a. Microsoft Office.",
    operators: ["is", "is not", "=", "!"],
    examples: [],
    related: [],
    regex: "^(?:32-bit|64-bit)$",
  },
  optional: {
    description:
      "Prevents a package from running automatically during an automated desired state mode. Optional packages can be run from the command-line by using the /include-optional parameter.  See the CLI documentation for details.",
    operators: [],
    examples: [],
    related: ["recommended"],
  },
  "OS architecture": {
    description: "Matches supported Windows architecture editions.",
    operators: ["is", "is not", "=", "!"],
    examples: [],
    related: [],
    regex: "^(?:x86|x64|ARM64)$",
  },
  "OS ver": {
    description: "Matches the currently running version of Windows. Accepts version comparison operators: <=, <, =, >, >=, !",
    operators: ["is", "is not", "<=", "<", "=", ">", ">=", "!"],
    examples: [],
    related: [],
    regex: "^(\\d+\\.)?(\\d+\\.)?(\\d+\\.)?(\\d+)$",
  },
  critical: {
    description: "",
    operators: [],
    examples: [],
    related: [],
  },
  recommended: {
    description:
      "This keyword may be used to improve readability of some rules, but it is not required, because it is the default state of any applicable package. Allows a package to run automatically during an automated desired state mode.",
    operators: [],
    examples: [],
    related: ["optional"],
  },
  server: {
    description: "Any server-class Windows OS.",
    operators: [],
    examples: [],
    related: ["endpoint", "workstation", "VDI", "VM"],
  },
  site: {
    description:
      "Matches the name of a subnet the endpoint is connected to, as defined by either the [Sites and Subnet] section of the policy, or a subnet defined by Active Directory Sites and Services. Regular expressions are allowed and must match the full site name. This condition supports writing the value alone.",
    operators: ["is", "is not", "=", "!"],
    examples: ["site = Azure.*", "Azure.*"],
    related: ["AD site"],
    regex: "^(?:[A-Z][A-Z0-9-]{0,61}[A-Z0-9])$",
  },
  "system config": {
    description:
      "Matches the name of the system configuration value as specified in the UI, at the command line, or by the HKLM\\Software\\Aiden:SystemConfig registry value. Partial values which begin with the actual value are allowed. Regular expressions are allowed. This condition supports writing the value alone.",
    operators: ["is", "is not", "=", "!"],
    examples: ["system config = IT-Lab", "IT-Lab", "IT"],
    related: [],
    regex: "",
  },
  workstation: {
    description:
      "Any workstation-class Windows OS (e.g. Windows 11), except for VDI systems as defined by Microsoft such as Citrix VMs, multi-session AVDs, etc. (see VDI).",
    operators: [],
    examples: [],
    related: ["endpoint", "server", "VDI", "VM"],
  },
  "using a cloud repo": {
    description: "Any device which is connected to an Aiden cloud-repo, as opposed to a local-repo or on-prem-repo.",
    operators: [],
    examples: [],
    related: [],
  },
  unattended: {
    description:
      "Runs a package visible to the end user. Will only pause if an error occurs. AidenBot must be running in interactive or unattended mode within an interactive user environment. Packages run in silent mode by default.",
    operators: [],
    examples: [],
    related: ["interactive"],
  },
  VDI: {
    description:
      "Any Azure-based multi-session Windows OS, any Remote Desktop based Session Host or Virtualization Host, any Citrix-based VDI endpoint, and any VMware-based VDI endpoint.",
    operators: [],
    examples: [],
    related: ["endpoint", "server", "workstation", "VM"],
  },
  "visibly installed": {
    description: "Same as 'installed', but ignores Win32 apps which are hidden from Programs and Features.",
    operators: ["is", "is not", "<=", "<", "=", ">", ">=", "!"],
    examples: [],
    related: ["installed"],
  },
  "VM or virtual machine": {
    description: "Any type of virtual machine, including VDI systems.",
    operators: [],
    examples: [],
    related: ["endpoint", "server", "workstation", "VDI"],
  },
  "Windows 10": {
    description: "Any device running Windows 10.",
    operators: [],
    examples: [],
    related: ["OS version", "Windows 11", "Windows Server 2022"],
  },
  "Windows 11": {
    description: "Any device running Windows 11.",
    operators: [],
    examples: [],
    related: ["OS version", "Windows 10", "Windows Server 2022"],
  },
  "Windows Server 2022": {
    description: "Any device running Windows Server 2022.",
    operators: [],
    examples: [],
    related: ["OS version", "Windows 10", "Windows 11"],
  },
  WinPE: {
    description: "Any device running Windows Preinstallation Environment.",
    operators: [],
    examples: [],
    related: [],
  },
  "allowed during the enrollment status page": {
    description:
      "Allowed at any time, including during the ESP – This phrase is useful when packages are not allowed during the ESP by default.",
    operators: [],
    examples: [],
    related: ["during an OS deployment, during Autopilot, during ESP Account Setup, during ESP Device Setup, during the enrollment status page, during Windows Setup"],
  },
};
